import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Mail, Copy, UserPlus, Crown } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABELS = {
  owner: "Inhaber", admin: "Administrator", accounting: "Buchhaltung",
  tax_advisor: "Steuerberater", team_readonly: "Team (lesend)",
};
const ROLE_COLORS = {
  owner: "bg-violet-100 text-violet-700",
  admin: "bg-blue-100 text-blue-700",
  accounting: "bg-emerald-100 text-emerald-700",
  tax_advisor: "bg-amber-100 text-amber-700",
  team_readonly: "bg-neutral-100 text-neutral-700",
};

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "team_readonly" });
  const [denied, setDenied] = useState(false);

  const load = async () => {
    try {
      const [u, i] = await Promise.all([api.get("/users"), api.get("/invites")]);
      setUsers(u.data); setInvites(i.data);
    } catch (e) {
      if (e.response?.status === 403) setDenied(true);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    try {
      const { data } = await api.post("/invites", form);
      if (data.email_sent) toast.success(`Einladung an ${form.email} versendet`);
      else toast.warning(`Einladung erstellt — E-Mail-Versand nicht möglich. Link manuell teilen.`);
      setOpen(false); setForm({ email: "", name: "", role: "team_readonly" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const revoke = async (id) => {
    if (!window.confirm("Einladung widerrufen?")) return;
    await api.delete(`/invites/${id}`); toast.success("Widerrufen"); load();
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/users/${uid}/role`, { role });
      toast.success("Rolle aktualisiert");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const removeUser = async (uid) => {
    if (!window.confirm("Benutzer wirklich entfernen?")) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("Entfernt"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("Link kopiert");
  };

  if (denied) return (
    <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800">
      Nur Admins und der Inhaber sehen das Team-Management.
    </div>
  );

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Team</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Benutzer & Einladungen</h1>
          <p className="text-sm text-neutral-500 mt-2">Lade Teammitglieder, Buchhaltung oder Steuerberater ein und steuere deren Berechtigungen.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="invite-btn"><UserPlus className="h-4 w-4 mr-2" />Einladen</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Benutzer einladen</DialogTitle>
              <DialogDescription>Die Einladung wird per E-Mail verschickt (falls SMTP/Resend konfiguriert ist) oder du teilst den Link manuell.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name" /></div>
              <div><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email" /></div>
              <div>
                <Label>Rolle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrator (alles außer Inhaber-Settings)</SelectItem>
                    <SelectItem value="accounting">Buchhaltung (Rechnungen, Zahlungen, Konten)</SelectItem>
                    <SelectItem value="tax_advisor">Steuerberater (lesend + Audit + USt)</SelectItem>
                    <SelectItem value="team_readonly">Team (nur lesend)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={invite} data-testid="invite-save">Einladung senden</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-4">Aktive Benutzer</div>
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="flex items-center gap-4 py-3 border-b border-neutral-100 last:border-0" data-testid={`user-row-${u.id}`}>
              <div className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: "var(--tenant-primary)" }}>{u.name.slice(0, 2).toUpperCase()}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium flex items-center gap-2">{u.name}{u.role === "owner" && <Crown className="h-3.5 w-3.5 text-amber-500" />}</div>
                <div className="text-xs text-neutral-500">{u.email}</div>
              </div>
              <Badge className={ROLE_COLORS[u.role]} variant="secondary">{ROLE_LABELS[u.role]}</Badge>
              {u.role !== "owner" && u.id !== user?.id && (
                <>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                    <SelectTrigger className="w-40 h-8" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrator</SelectItem>
                      <SelectItem value="accounting">Buchhaltung</SelectItem>
                      <SelectItem value="tax_advisor">Steuerberater</SelectItem>
                      <SelectItem value="team_readonly">Team (lesend)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => removeUser(u.id)} data-testid={`user-remove-${u.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {invites.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-4">Einladungen</div>
          <div className="space-y-2">
            {invites.map((inv) => (
              <div key={inv.id} className="flex items-center gap-3 py-3 border-b border-neutral-100 last:border-0" data-testid={`invite-row-${inv.id}`}>
                <Mail className="h-4 w-4 text-neutral-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{inv.email}</div>
                  <div className="text-xs text-neutral-500">{inv.name} · {fmtDate(inv.created_at)}</div>
                </div>
                <Badge className={ROLE_COLORS[inv.role]} variant="secondary">{ROLE_LABELS[inv.role]}</Badge>
                <Badge variant="outline" className={inv.status === "pending" ? "border-amber-300 text-amber-700" : inv.status === "accepted" ? "border-emerald-300 text-emerald-700" : "border-neutral-300 text-neutral-500"}>
                  {inv.status === "pending" ? "Offen" : inv.status === "accepted" ? "Angenommen" : "Widerrufen"}
                </Badge>
                {inv.status === "pending" && (
                  <>
                    {inv.accept_url && <Button size="icon" variant="ghost" onClick={() => copyLink(inv.accept_url)} title="Einladungslink kopieren" data-testid={`invite-copy-${inv.id}`}><Copy className="h-4 w-4" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => revoke(inv.id)} data-testid={`invite-revoke-${inv.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
